import { Assignment, Edit, House, Person } from "@mui/icons-material";
import { Button, Divider, Grid, IconButton, Link, Stack, Typography } from "@mui/material";
import useOrderStore from "../../../stores/useOrderStore";
import { useEffect, useState } from "react";
import OrderCheckout from "./OrderCheckout";
import ReservationDateRange from "../../text/ReservationDateRange";
import { useNavigate } from "react-router-dom";
import OrderTip from "./OrderTip";
import OrderStatusChip from "./OrderStatusChip";
import show from "../../../api/auth/orders/show";
import useAlertStore from "../../../stores/useAlertStore";
import EditableReservationItemCost from "./EditableReservationItemCost";
import paidAlready from "../../../api/auth/orders/paidAlready";
import DeleteConfirmation from "../../form/DeleteConfirmation";
import index from "../../../api/auth/orders/reservations";
import indexOrders from "../../../api/auth/orders";

import EditableAdminComments from "./EditableAdminComments";

export default function OrderSummary({ order, orderId }) {
  const { updateOrder, setReservations, reservations, orderById, orders, setOrders } = useOrderStore();
  const [ checkingOut, setCheckingOut ] = useState(false);

  if(!order) {
    order = orderById(orderId);
  }

  const { setAlertMessage } = useAlertStore();

  const navigate = useNavigate();

  const handleCheckout = () => {
    setCheckingOut(true);
  };

  const handleViewReceipt = () => {
    window.open(order.payment_intent_receipt_url, '_blank');
  };

  const handleViewPayment = () => {
    window.open(`https://dashboard.stripe.com/payments/${order.payment_intent_id}`, '_blank');
  };

  const handleAlreadyPaid = () => {
    paidAlready(order.id, (response) => {
      updateOrder(response);
      setAlertMessage("Order updated successfully", "success");
    });
  };

  const refreshOrder = () => {
    show(order.id, (response) => {
      updateOrder(response);
      setCheckingOut(false);
      setAlertMessage("Order updated successfully", "success");
    });
  };

  useEffect(() => {
    if(!orders || orders.length === 0) {
      indexOrders((response) => {
        setOrders(response);
      });
    }
  });

  useEffect(() => {
    if(order) {
      index(order.id, (response) => {
        setReservations(response);
      });
    } else {
      index(orderId, (response) => {
        setReservations(response);
      });
    }

  }, [order?.id, orderId]);

  if(!reservations || !orders || orders.length === 0) return null;

  return (
    <Stack spacing={2}>
      <Stack>
        <Typography variant="h5">
          Order Summary #{order.id}
        </Typography>
      </Stack>
      <Stack direction="row">
        <OrderStatusChip order={order} showCheckoutStatus />
      </Stack>
      <Stack>
        {order.customer_id && <Grid container>
          <Grid item sm={1} display="flex">
            <IconButton size="small" onClick={() => navigate(`/customers/${order.customer_id}`)}>
              <Person sx={{ my: "auto" }} />
            </IconButton>
          </Grid>
          <Grid item sm={11} display="flex">
            <Stack>
              <Typography variant="h6" sx={{ my: "auto" }}>
                {order.customer.full_name}
              </Typography>
              <Typography variant="caption" sx={{ my: "auto" }}>
                {order.customer.phone}
              </Typography>
            </Stack>
          </Grid>
        </Grid>}
        {order.location_id && <Grid container>
          <Grid item sm={1} display="flex">
            <IconButton size="small" onClick={() => navigate(`/locations/${order.location_id}`)}>
              <House sx={{ my: "auto" }} />
            </IconButton>
          </Grid>
          <Grid item sm={11} display="flex" flexDirection="column">
            <Typography variant="h6" sx={{ my: "auto" }}>
              {order.location.name}
            </Typography>
            <Typography variant="body1" sx={{ my: "auto" }}>
              {order.location.address_1}, {order.location.city}, {order.location.state} {order.location.zip}
            </Typography>
          </Grid>
        </Grid>}
      </Stack>
      {(reservations).length > 0 && <Stack gap={2}>
        {(reservations).map((reservation) => (
          <Stack key={reservation.id} direction="row" width="100%">
            {reservation.order?.status !== 'draft' && <IconButton onClick={() => navigate(`/reservations/${reservation.id}`)}>
              <Edit />
            </IconButton>}
            <Stack width="100%">
              <Stack direction="row" spacing={2}>
                {reservation.start_at ? <ReservationDateRange reservation={reservation} />:
                <Typography variant="body1" fontWeight="700">
                  Unscheduled reservation
                </Typography>}
                <Stack direction="row" spacing={2}>
                {reservation?.reservation_waivers?.length > 0 && reservation?.reservation_waivers.map((reservationWaiver) => (
                  <Stack direction="row" key={reservationWaiver.id} spacing={0.5}>
                    <Assignment />
                    <Link key={reservationWaiver.id} href={`/waivers/${reservationWaiver.waiver_uuid}/${reservationWaiver.id}`}>
                      <Typography variant="body1">
                        {reservationWaiver.waiver_name} {reservationWaiver.signed_by_image_url && `(signed)`}
                      </Typography>
                    </Link>
                  </Stack>
                  ))}
              </Stack>
              </Stack>
              <Stack>
                <Typography variant="caption">
                  {reservation.admin_comments}
                </Typography>
              </Stack>
              <Stack pl={1} mb={1}>
                {reservation.reservation_items.map((reservationItem) => (
                  <Grid container key={reservationItem.id}>
                    <Grid item xs={6} sm={10}>
                      <Typography variant="body1">
                        {reservationItem.quantity}x {reservationItem.item?.name} ({reservation?.bookable_duration?.name})
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <EditableReservationItemCost reservationItem={reservationItem} refreshOrder={refreshOrder}/>
                    </Grid>
                  </Grid>
                ))}
              </Stack>
              {reservation.reservation_answers?.length > 0 && <Stack>
                {reservation.reservation_answers.map((answer) => (
                  <Stack key={answer.id} direction="row" spacing={1}>
                    <Typography variant="body1" fontWeight="700">
                      {answer.reservation_question.question}
                    </Typography>
                    <Typography variant="body1">
                      {answer.answer}
                    </Typography>
                  </Stack>
                ))}
              </Stack>}
            </Stack>
          </Stack>
        ))}
      </Stack>}
      <Grid container>
        <Grid item xs={6} sm={10}>
          <Typography variant="h6" fontWeight="700">
            Revenue
          </Typography>
        </Grid>
        <Grid item xs={6} sm={2}>
          <Typography variant="h6">
            ${order.total_price_dollars}
          </Typography>
        </Grid>
      </Grid>
      {order.status !== 'paid' && <Stack>
        <OrderTip order={order} />
      </Stack>}
      <Stack>
        <Grid container mb={2}>
          <Grid item xs={6} sm={10}>
            <Typography variant="body1">
              Stripe fee
            </Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography variant="body1">
              ${order.stripe_service_fee_dollars}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={10}>
            <Typography variant="body1">
              WUSH fee
            </Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography variant="body1">
              ${order.wush_application_fee_dollars}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={10}>
            <Typography variant="body1">
              State taxes
            </Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography variant="body1">
              ${order.total_state_tax_dollars}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={10}>
            <Typography variant="body1">
              Local taxes
            </Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography variant="body1">
              ${order.total_local_tax_dollars}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={10}>
            <Typography variant="body1">
              Misc taxes
            </Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography variant="body1">
              ${order.total_misc_tax_dollars}
            </Typography>
          </Grid>
        </Grid>
        <Stack mb={1} width={{ xs: "100%", sm: "75%" }}>
          <EditableAdminComments order={order} />
        </Stack>
      </Stack>
      <Stack>
        <Grid container>
          <Grid item xs={6} sm={10}>
            <Typography variant="h5" fontWeight="700">
              Total
            </Typography>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Typography variant="h5">
              ${order.grand_total_price_dollars}
            </Typography>
          </Grid>
        </Grid>
      </Stack>
      <Stack direction="row" gap={2}>
        {order.payment_intent_receipt_url && <Stack direction="row">
          <Button onClick={handleViewReceipt} variant="contained" color="secondary" className="hide-print">
            View Receipt
          </Button>
        </Stack>}
        {order.payment_intent_id && order.status === 'paid' && <Stack direction="row">
          <Button onClick={handleViewPayment} variant="outlined" color="secondary" className="hide-print">
            View payment in Stripe
          </Button>
        </Stack> }
      </Stack>
      {order.ready_for_payment && <Stack>
        <Stack my={2}>
          <Divider />
        </Stack>
        {checkingOut ? <Stack>
          <OrderCheckout order={order} afterSuccess={refreshOrder} />
        </Stack> :
        <Stack>
          <Stack mb={2}>
            <Button onClick={handleCheckout} variant="contained" color="primary">
              Checkout
            </Button>
          </Stack>
          <Stack direction="row">
            <DeleteConfirmation
              handleDelete={handleAlreadyPaid}
              title="Are you sure you want to mark this order as paid?"
              ctaText="Mark as Paid"
              ctaColor="success"
            >
              <Button variant="outlined">
                Confirm Paid
              </Button>
            </DeleteConfirmation>
          </Stack>
        </Stack>}
      </Stack>}
    </Stack>
  );
}